import { Suspense } from 'react';
import { useSuspenseQuery } from '@tanstack/react-query';
import { Box } from '@givebutter/primitives/box';
import { Button } from '@givebutter/primitives/button';
import { fetchWorkflowActionMetrics } from 'api/workflows';
import * as ActionMetricsRow from 'assets/js/pages/engage/workflows/components/ActionMetricsRow';
import { WorkflowMessagePreview } from 'assets/js/pages/engage/workflows/components/WorkflowMessagePreview';
import * as Dialog from 'components/dialog';
import { DialogSkeleton } from 'components/dialog-route';
import { WorkflowActionTypeEnum } from 'constants/workflows';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentWorkflow } from 'customHooks/useCurrentWorkflow';
import { workflowQueryKeys } from 'queries/workflow-query-keys';

const WorkflowDetailsActionMetrics = () => {
    const { accountId } = useCurrentAccount();
    const { data: workflow } = useCurrentWorkflow();

    const { data: actionMetrics } = useSuspenseQuery({
        queryKey: workflowQueryKeys.workflowActionMetricsById(accountId, workflow.id),
        queryFn: () => fetchWorkflowActionMetrics(accountId, workflow.id),
    });

    if (actionMetrics.length === 0) return null;

    return (
        <Box className="overflow-hidden">
            <div className="flex items-start justify-between border-b p-6 py-4">
                <h2 className="text-xl font-medium">Messages</h2>
            </div>
            <div className="flex flex-col">
                {actionMetrics.map(row => {
                    return (
                        <ActionMetricsRow.Root
                            defaultOpen={actionMetrics.length === 1}
                            key={row.id}
                            row={row}
                        >
                            <div className="flex justify-between">
                                <div className="flex flex-col gap-2">
                                    <ActionMetricsRow.Title />
                                    <ActionMetricsRow.Description />
                                </div>
                                <div className="flex gap-2">
                                    <Dialog.Root>
                                        <Dialog.Trigger asChild>
                                            <Button variant="transparent" size="sm">
                                                Preview
                                            </Button>
                                        </Dialog.Trigger>
                                        <Dialog.Portal>
                                            <Dialog.Overlay />
                                            <Dialog.Content className="min-h-60 max-w-screen-md">
                                                <Dialog.Close className="top-6" />
                                                <Dialog.Title className="text-lg sm:text-lg">
                                                    {row.action === WorkflowActionTypeEnum.SEND_SMS
                                                        ? 'Text'
                                                        : 'Email'}{' '}
                                                    preview
                                                </Dialog.Title>
                                                <div className="py-8">
                                                    <Suspense fallback={<DialogSkeleton />}>
                                                        <WorkflowMessagePreview
                                                            messageId={row.message.id}
                                                        />
                                                    </Suspense>
                                                </div>
                                            </Dialog.Content>
                                        </Dialog.Portal>
                                    </Dialog.Root>
                                    {actionMetrics.length > 1 && (
                                        <ActionMetricsRow.CollapsibleTrigger />
                                    )}
                                </div>
                            </div>
                            <ActionMetricsRow.Content />
                        </ActionMetricsRow.Root>
                    );
                })}
            </div>
        </Box>
    );
};

export { WorkflowDetailsActionMetrics };
